/**
 * Platform Capabilities Bento Scroll Animation Engine
 * Strictly Plain Vanilla JavaScript & Tailwind CSS
 * 
 * Crisp True-Dimension Morphing Architecture:
 * - Eliminates CSS `scale()` magnification blur entirely.
 * - Silky smoothstep Hermite easing on header fade and upward grid float (zero shake/jitter).
 * - Direct GPU-accelerated RAF transforms with zero conflicting CSS transition delays.
 * - Physically morphs `width`, `height`, and `translate3d(tx, ty, 0)` on the single hero card.
 * - All fonts, KPI figures, and SVG graphics re-rasterize natively at 100% razor-sharp screen resolution.
 */

(function () {
  'use strict';

  function initCapabilitiesBento() {
    const section = document.getElementById('platform-capabilities-bento');
    const stickyContainer = document.getElementById('cap-sticky-container');
    const bentoGrid = document.getElementById('cap-bento-grid');
    const heroCard = document.getElementById('cap-hero-card');
    const header = document.getElementById('cap-header');
    const compactBody = document.getElementById('cap-compact-body');
    const expandedBody = document.getElementById('cap-expanded-body');
    const footerHint = document.getElementById('cap-footer-hint');

    const card1 = document.getElementById('cap-card-1'); // Left Column (spans 2 rows): AI Powered Accurate Commissions
    const card2 = document.getElementById('cap-card-2'); // Top Right-Left: E-wallet Multi-Currency Support
    const card3 = document.getElementById('cap-card-3'); // Top Right-Right: Email and SMS Integrations
    const card6 = document.getElementById('cap-card-6'); // Bottom Right: Security Backups and SLA

    if (!section || !heroCard || !stickyContainer) return;

    let heroRect = { width: 0, height: 0, centerX: 0, centerY: 0 };
    let targetMetrics = { deltaX: 0, deltaY: 0, targetWidth: 1140, targetHeight: 580 };
    let vpWidth = window.innerWidth;
    let vpHeight = window.innerHeight;
    let isTicking = false;
    let lastProgress = -1;
    const maxGridShift = 40; // px upward shift to compensate for faded header without over-shifting

    // Measure natural DOM layout positions cleanly without transforms or custom sizes
    function measureLayout() {
      vpWidth = window.innerWidth;
      vpHeight = window.innerHeight;

      // Reset inline sizing to measure natural Bento grid dimensions
      heroCard.style.width = '';
      heroCard.style.maxWidth = '';
      heroCard.style.minWidth = '';
      heroCard.style.height = '';
      heroCard.style.transform = 'none';

      if (bentoGrid) bentoGrid.style.transform = 'none';
      if (card1) card1.style.transform = 'none';
      if (card2) card2.style.transform = 'none';
      if (card3) card3.style.transform = 'none';
      if (card6) card6.style.transform = 'none';

      const stickyRect = stickyContainer.getBoundingClientRect();
      const hRect = heroCard.getBoundingClientRect();

      heroRect.width = hRect.width;
      heroRect.height = hRect.height;
      const heroLeft = hRect.left - stickyRect.left;
      const heroTop = hRect.top - stickyRect.top;

      // Target size for expanded state (contained within max-w-[1140px], rounded-[2rem])
      const targetWidth = Math.min(1140, Math.max(320, vpWidth - 32));
      const targetHeight = Math.min(510, Math.max(320, Math.min(vpHeight * 0.70, 510)));

      // Horizontal target: exactly centered inside stickyContainer/viewport
      const targetLeft = (stickyRect.width - targetWidth) / 2;

      // Vertical target: aligned towards top with clean navbar clearance across mobile/iPad/desktop
      const targetTop = vpWidth < 640 ? 100 : (vpWidth < 1024 ? 110 : 120);

      targetMetrics.targetWidth = targetWidth;
      targetMetrics.targetHeight = targetHeight;
      targetMetrics.deltaX = targetLeft - heroLeft;
      // Factor in the -maxGridShift applied to bentoGrid
      targetMetrics.deltaY = targetTop - (heroTop - maxGridShift);

      // Render current state
      render(calculateProgress(), true);
    }

    // Normalized progress in [0, 1] over the section
    function calculateProgress() {
      const rect = section.getBoundingClientRect();
      const stickyHeight = stickyContainer.offsetHeight || 760;
      const totalScrollable = Math.max(1, section.offsetHeight - stickyHeight);

      const scrolled = -rect.top;
      const progress = scrolled / totalScrollable;
      return Math.max(0, Math.min(1, progress));
    }

    // Apply 60fps GPU transforms and smooth single-card physical resizing
    function render(progress, force) {
      if (!force && Math.abs(progress - lastProgress) < 0.00001) return;
      lastProgress = progress;

      if (vpWidth < 768) {
        if (header) { header.style.opacity = ''; header.style.transform = ''; }
        if (bentoGrid) { bentoGrid.style.transform = ''; }
        heroCard.style.width = ''; heroCard.style.maxWidth = ''; heroCard.style.minWidth = ''; heroCard.style.height = ''; heroCard.style.transform = ''; heroCard.style.zIndex = '';
        if (compactBody) { compactBody.style.display = ''; compactBody.style.opacity = ''; }
        if (expandedBody) { expandedBody.style.display = ''; expandedBody.style.opacity = ''; }
        if (card1) { card1.style.transform = ''; card1.style.opacity = ''; }
        if (card2) { card2.style.transform = ''; card2.style.opacity = ''; }
        if (card3) { card3.style.transform = ''; card3.style.opacity = ''; }
        if (card6) { card6.style.transform = ''; card6.style.opacity = ''; }
        return;
      }

      // ─── 1. SECTION HEADER & BENTO GRID UPWARD SHIFT (p = 0.01 -> 0.08) ───
      let gridShiftY = 0;
      if (progress <= 0.01) {
        if (header) {
          header.style.opacity = '1';
          header.style.transform = 'translate3d(0px, 0px, 0px)';
        }
        if (bentoGrid) {
          bentoGrid.style.transform = 'translate3d(0px, 0px, 0px)';
        }
      } else if (progress <= 0.08) {
        const rawHp = (progress - 0.01) / 0.07;
        // Smoothstep Hermite curve: 3x^2 - 2x^3 creates zero initial & terminal jerk
        const smoothHp = rawHp * rawHp * (3 - 2 * rawHp);
        const ty = -45 * smoothHp;
        
        if (header) {
          header.style.opacity = Math.max(0, 1 - smoothHp).toFixed(3);
          header.style.transform = `translate3d(0px, ${ty.toFixed(2)}px, 0px)`;
        }
        gridShiftY = -maxGridShift * smoothHp;
        if (bentoGrid) {
          bentoGrid.style.transform = `translate3d(0px, ${gridShiftY.toFixed(2)}px, 0px)`;
        }
      } else {
        if (header) {
          header.style.opacity = '0';
          header.style.transform = 'translate3d(0px, -45px, 0px)';
        }
        gridShiftY = -maxGridShift;
        if (bentoGrid) {
          bentoGrid.style.transform = `translate3d(0px, -${maxGridShift}px, 0px)`;
        }
      }

      // ─── 2. FAST & RESPONSIVE ZOOM TIMING (Starts at p = 0.02, fully expanded by p = 0.42) ───
      let t = 0;
      if (progress > 0.02) {
        t = Math.min(1, (progress - 0.02) / 0.40);
      }

      // ─── 3. CRISP PHYSICAL CARD RESIZING (NO CSS SCALE = 100% RAZOR SHARP) ───
      if (t <= 0.001) {
        heroCard.style.width = '';
        heroCard.style.maxWidth = '';
        heroCard.style.minWidth = '';
        heroCard.style.height = '';
        heroCard.style.transform = 'translate3d(0px, 0px, 0px)';
        heroCard.style.zIndex = '25';

        if (compactBody) {
          compactBody.style.display = 'flex';
          compactBody.style.opacity = '1';
        }
        if (expandedBody) {
          expandedBody.style.display = 'none';
          expandedBody.style.opacity = '0';
        }
        if (footerHint) {
          footerHint.textContent = 'Scroll to expand';
        }
      } else {
        const curWidth = heroRect.width + (targetMetrics.targetWidth - heroRect.width) * t;
        const curHeight = heroRect.height + (targetMetrics.targetHeight - heroRect.height) * t;
        const curTx = targetMetrics.deltaX * t;
        const curTy = targetMetrics.deltaY * t;

        heroCard.style.width = `${curWidth.toFixed(1)}px`;
        heroCard.style.maxWidth = `${curWidth.toFixed(1)}px`;
        heroCard.style.minWidth = `${curWidth.toFixed(1)}px`;
        heroCard.style.height = `${curHeight.toFixed(1)}px`;
        heroCard.style.transform = `translate3d(${curTx.toFixed(2)}px, ${curTy.toFixed(2)}px, 0px)`;
        heroCard.style.zIndex = '50';

        // ─── Non-Overlapping Inner Content Transition ───
        if (t < 0.18) {
          if (compactBody) {
            compactBody.style.display = 'flex';
            compactBody.style.opacity = (1 - t / 0.18).toFixed(3);
          }
          if (expandedBody) {
            expandedBody.style.display = 'none';
            expandedBody.style.opacity = '0';
          }
        } else {
          if (compactBody) {
            compactBody.style.display = 'none';
            compactBody.style.opacity = '0';
          }
          if (expandedBody) {
            expandedBody.style.display = 'flex';
            const expOp = Math.min(1, (t - 0.18) / 0.25);
            expandedBody.style.opacity = expOp.toFixed(3);
          }
        }

        if (footerHint) {
          footerHint.textContent = t > 0.40 ? 'Scroll up to return to Bento Grid' : 'Scroll to expand';
        }
      }

      // ─── 4. SNAPPY RADIAL DISPERSAL OF 4 SIDE CARDS WITH ZOOM-IN & SWIFT FADE ───
      const cardScale = (1.0 + 0.10 * Math.min(1, t * 1.5)).toFixed(3);
      const moveT = Math.pow(t, 0.95);
      const pushX = vpWidth * 0.45 + 160;
      const pushY = vpHeight * 0.40 + 120;
      const cardOpacity = Math.max(0, 1 - Math.pow(t, 0.9) * 1.9).toFixed(3);

      // Top cards lift upward with a gentle ease-out curve for a natural widening gap
      const topMoveT = 1 - Math.pow(1 - t, 1.2);
      const pushYTop = pushY * 1.25 + 50;

      if (card1) {
        // Card 1: Left Column -> Move Left out of frame with swift fade & scale
        const tx = (-(pushX * 1.1) * moveT).toFixed(2);
        const ty = (0).toFixed(2);
        card1.style.transform = `translate3d(${tx}px, ${ty}px, 0px) scale(${cardScale})`;
        card1.style.opacity = cardOpacity;
      }

      if (card2) {
        // Card 2: Top Left -> Lift Upward and Left with expanding gap
        const tx = (-(pushX * 0.48) * moveT).toFixed(2);
        const ty = (-(pushYTop) * topMoveT).toFixed(2);
        card2.style.transform = `translate3d(${tx}px, ${ty}px, 0px) scale(${cardScale})`;
        card2.style.opacity = cardOpacity;
      }

      if (card3) {
        // Card 3: Top Right -> Lift Upward and Right with expanding gap
        const tx = ((pushX * 0.92) * moveT).toFixed(2);
        const ty = (-(pushYTop) * topMoveT).toFixed(2);
        card3.style.transform = `translate3d(${tx}px, ${ty}px, 0px) scale(${cardScale})`;
        card3.style.opacity = cardOpacity;
      }

      if (card6) {
        // Card 6: Bottom Right -> Move Right with slight downward drift, avoiding bottom floor clipping
        const tx = ((pushX * 1.1) * moveT).toFixed(2);
        const ty = ((pushY * 0.15) * moveT).toFixed(2);
        card6.style.transform = `translate3d(${tx}px, ${ty}px, 0px) scale(${cardScale})`;
        card6.style.opacity = cardOpacity;
      }
    }

    // Scroll listener with RAF throttling
    function onScroll() {
      if (!isTicking) {
        window.requestAnimationFrame(() => {
          const progress = calculateProgress();
          render(progress);
          isTicking = false;
        });
        isTicking = true;
      }
    }

    // Resize listener with debounce
    let resizeTimer;
    function onResize() {
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(() => {
        measureLayout();
      }, 50);
    }

    // Bind listeners
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onResize, { passive: true });

    if (window.lenis && typeof window.lenis.on === 'function') {
      window.lenis.on('scroll', onScroll);
    }

    measureLayout();
    window.addEventListener('load', measureLayout);
  }

  // Auto-init
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initCapabilitiesBento);
  } else {
    initCapabilitiesBento();
  }
})();
